import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Chip, Checkbox, FormControlLabel,
} from '@material-ui/core';
import AgentVerdictCard from './AgentVerdictCard';
import { SERVER_NAME } from './config';

const API_BASE = SERVER_NAME;

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 20px 60px',
    color: '#e5e7ff',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: -0.5,
    color: '#e5e7ff',
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#9ea4d8',
    marginBottom: 24,
  },
  summaryRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  statCard: {
    background: 'rgba(10, 14, 30, 0.92)',
    border: '1px solid rgba(77,121,255,0.22)',
    borderRadius: 12,
    padding: '12px 18px',
    minWidth: 110,
  },
  statLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#9ea4d8',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 900,
    color: '#e5e7ff',
  },
  filtersRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 20,
    marginTop: 10,
  },
  select: {
    minWidth: 140,
    '& .MuiInputBase-root': {
      color: '#fff',
      fontSize: 13,
      background: 'rgba(8, 12, 28, 0.92)',
      borderRadius: 12,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(130,160,255,0.75)' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d79ff' },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(77,121,255,0.45)' },
    '& .MuiInputLabel-outlined': { color: '#9ea4d8' },
    '& .MuiSelect-icon': { color: '#cfd8ff' },
  },
  menuPaper: {
    marginTop: 8,
    borderRadius: 12,
    background: 'rgba(20, 28, 62, 0.98)',
    color: '#fff',
    border: '1px solid rgba(77,121,255,0.35)',
    boxShadow: '0 18px 44px rgba(0,0,0,0.45)',
    '& .MuiMenuItem-root': {
      color: '#fff',
      fontSize: 14,
      padding: '10px 18px',
      '&:hover': { background: 'rgba(77,121,255,0.22)' },
      '&.Mui-selected': {
        background: 'rgba(77,121,255,0.32)',
        '&:hover': { background: 'rgba(77,121,255,0.38)' },
      },
    },
  },
  checkboxLabel: {
    color: '#e5e7ff',
    fontSize: 12,
    marginLeft: 2,
    marginRight: 2,
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      fontWeight: 700,
      color: '#e5e7ff',
    },
    '& .MuiCheckbox-root': {
      color: '#9ea4d8',
      padding: 6,
    },
    '& .MuiCheckbox-colorPrimary.Mui-checked': {
      color: '#9ef7c5',
    },
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#e5e7ff',
    marginBottom: 12,
    marginTop: 24,
  },
  empty: {
    fontSize: 13,
    color: '#9ea4d8',
    marginTop: 20,
    textAlign: 'center',
    padding: '40px 0',
  },
}));

function SummaryCards({ summary }) {
  const classes = useStyles();
  if (!summary) return null;

  const totalReviewed = (summary.actions || []).reduce((s, r) => s + Number(r.count), 0);
  const paperBuys     = (summary.actions || []).filter(r => r.action_type === 'PAPER_BUY').reduce((s, r) => s + Number(r.count), 0);
  const rejections    = (summary.actions || []).filter(r => r.action_type === 'REJECT').reduce((s, r) => s + Number(r.count), 0);
  const validated     = (summary.outcomes || []).filter(r => r.verdict === 'validated').reduce((s, r) => s + Number(r.count), 0);
  const invalidated   = (summary.outcomes || []).filter(r => r.verdict === 'invalidated').reduce((s, r) => s + Number(r.count), 0);
  const winRate       = (validated + invalidated) > 0
    ? Math.round((validated / (validated + invalidated)) * 100) + '%'
    : '—';

  return (
    <div className={classes.summaryRow}>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Reviewed (7d)</div>
        <div className={classes.statValue}>{totalReviewed}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Paper Buys</div>
        <div className={classes.statValue} style={{ color: '#9ef7c5' }}>{paperBuys}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Rejections</div>
        <div className={classes.statValue} style={{ color: '#9ea4d8' }}>{rejections}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Validated</div>
        <div className={classes.statValue} style={{ color: '#9ef7c5' }}>{validated}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Win Rate</div>
        <div className={classes.statValue} style={{ color: '#4d79ff' }}>{winRate}</div>
      </div>
    </div>
  );
}

export default function AgentVerdictsPage() {
  const classes = useStyles();
  const location = useLocation();

  const [summary, setSummary] = useState(null);
  const [verdicts, setVerdicts] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [sideFilter, setSideFilter] = useState('');
  const [showRejectionsOnly, setShowRejectionsOnly] = useState(false);
  const [showValidatedOnly, setShowValidatedOnly] = useState(false);
  const actionIdFilter = String(new URLSearchParams(location.search || '').get('action_id') || '').trim();

  useEffect(() => {
    fetch(`${API_BASE}/agent-verdicts/summary`)
      .then(r => r.json())
      .then(setSummary)
      .catch(() => {});
  }, []);

  const loadVerdicts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 50 });
    if (actionTypeFilter) params.set('action_type', actionTypeFilter);
    if (sideFilter)       params.set('simulated_side', sideFilter);
    if (actionIdFilter)   params.set('action_id', actionIdFilter);

    fetch(`${API_BASE}/agent-verdicts?${params}`)
      .then(r => r.json())
      .then(data => {
        setVerdicts(data.verdicts || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [actionIdFilter, actionTypeFilter, sideFilter]);

  useEffect(() => { loadVerdicts(); }, [loadVerdicts]);

  const filteredVerdicts = verdicts.filter((verdict) => {
    if (!showRejectionsOnly && !showValidatedOnly) return true;
    const isRejection = verdict.action_type === 'REJECT';
    const isValidated = (outcomes[String(verdict.action_id)] || []).some(o => o.verdict === 'validated');
    return (showRejectionsOnly && isRejection) || (showValidatedOnly && isValidated);
  });

  useEffect(() => {
    const withTrade = verdicts.filter(v => v.trade_id);
    if (!withTrade.length) return;

    const fetches = withTrade.map(v =>
      fetch(`${API_BASE}/agent-verdicts/${v.action_id}/outcome`)
        .then(r => r.ok ? r.json() : null)
        .then(data => data ? [String(v.action_id), data.outcomes || []] : null)
        .catch(() => null)
    );
    Promise.all(fetches).then(results => {
      const map = {};
      for (const r of results) { if (r) map[r[0]] = r[1]; }
      setOutcomes(prev => ({ ...prev, ...map }));
    });
  }, [verdicts]);

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Agent Verdicts</Typography>
      <Typography className={classes.pageSubtitle}>
        Simulated capital decisions produced by the E3D Agent Verdict Engine — approved buys, rejected candidates, and tracked outcomes.
      </Typography>

      <SummaryCards summary={summary} />

      <div className={classes.filtersRow}>
        {actionIdFilter ? (
          <Chip
            size="small"
            label={`Action ID: ${actionIdFilter}`}
            style={{ background: 'rgba(158,247,197,0.12)', color: '#cfd8ff', border: '1px solid rgba(158,247,197,0.28)' }}
          />
        ) : null}
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Action Type</InputLabel>
          <Select
            value={actionTypeFilter}
            onChange={e => setActionTypeFilter(e.target.value)}
            label="Action Type"
            MenuProps={{ classes: { paper: classes.menuPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PAPER_BUY">Paper Buy</MenuItem>
            <MenuItem value="PAPER_SELL">Paper Sell</MenuItem>
            <MenuItem value="PAPER_EXIT">Paper Exit</MenuItem>
            <MenuItem value="REJECT">Reject</MenuItem>
            <MenuItem value="WAIT">Wait</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Side</InputLabel>
          <Select
            value={sideFilter}
            onChange={e => setSideFilter(e.target.value)}
            label="Side"
            MenuProps={{ classes: { paper: classes.menuPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="buy">Buy</MenuItem>
            <MenuItem value="sell">Sell</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          className={classes.checkboxLabel}
          control={<Checkbox color="primary" checked={showRejectionsOnly} onChange={e => setShowRejectionsOnly(e.target.checked)} />}
          label="Rejections"
        />

        <FormControlLabel
          className={classes.checkboxLabel}
          control={<Checkbox color="primary" checked={showValidatedOnly} onChange={e => setShowValidatedOnly(e.target.checked)} />}
          label="Validated"
        />

        {(actionTypeFilter || sideFilter || showRejectionsOnly || showValidatedOnly) ? (
          <Chip
            size="small"
            label="Clear filters"
            onClick={() => { setActionTypeFilter(''); setSideFilter(''); setShowRejectionsOnly(false); setShowValidatedOnly(false); }}
            style={{ background: 'rgba(77,121,255,0.15)', color: '#cfd8ff', border: '1px solid rgba(77,121,255,0.35)', cursor: 'pointer' }}
          />
        ) : null}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
          <CircularProgress size={28} style={{ color: '#4d79ff' }} />
        </div>
      ) : !filteredVerdicts.length ? (
        <Typography className={classes.empty}>No verdicts found with current filters.</Typography>
      ) : (
        <>
          <Typography className={classes.sectionTitle}>{`${filteredVerdicts.length} verdict${filteredVerdicts.length !== 1 ? 's' : ''}`}</Typography>
          {filteredVerdicts.map(verdict => (
            <AgentVerdictCard
              key={verdict.action_id}
              verdict={verdict}
              outcomes={outcomes[String(verdict.action_id)] || null}
            />
          ))}
        </>
      )}
    </div>
  );
}
